import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';
import { Location } from '@angular/common';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { KanbanStrategyService } from '../../services/kanban.strategy.service';
import { TrackerService } from '../../services/tracker.service';
import { TrackeritemService } from '../../services/trackeritem.service';
import { TrackeritemQueueService } from '../../services/trackeritem.queue.service';
import { UiService } from '../../../../core/services/core.ui.service';
import { UserService } from '../../../../core/services/core.user.service';
import { GfTranslateService } from '../../../../core/services/core.translate.service';
import { StateService } from '@uirouter/angular';
import { TrackeritemAddModalComponent } from '../../trackerItemAdd/trackerItemAddModal/trackeritem.add.modal.component';
import { TrackeritemEditModalComponent } from '../../edit/trackerItemEditModal/trackeritem.edit.modal.component';

import * as _ from 'lodash';

@Component({
  selector: 'trackeritem-browse',
  template: require('./trackeritem.browse.component.html')
})
export class TrackeritemBrowseComponent implements OnInit {
  @Input() trackeritems: any;
  @Input() tracker: any;
  @Input() project: any;
  @Input() view: string = 'grid';
  @Input() options: any = {};

  @Output() onRefresh: EventEmitter<any> = new EventEmitter<any>();;
  @Output() onUpdate: EventEmitter<any> = new EventEmitter<any>();;

  selectedItems: any[] = [];
  priorities: any[];
  projectTrackers: any;
  acls: any;
  autoRefresh: number = 1;
  autoRefreshPreferenceKey: string = '';
  nobody: any;
  assignees: any;
  pageSize: number;
  kanbanOptions: any;
  allChecked: boolean = false;
  updateSubmitted: boolean = false;

  constructor(
    private stateService: StateService,
    private kanbanStrategyService: KanbanStrategyService,
    private trackerService: TrackerService,
    private trackeritemService: TrackeritemService,
    private trackeritemQueueService: TrackeritemQueueService,
    private translateService: GfTranslateService,
    private uiService: UiService,
    private userService: UserService,
    private modalService: NgbModal,
    private location: Location
  ) {}

  ngOnInit() {
    if (!this.view) {
      this.view = 'grid';
    }
    this.priorities = this.trackerService.fields.builtIn.priority.getList();
    this.projectTrackers = _.get(this.project, 'rel.permissions.trackers', {});
    this.acls = this.trackerService.acl.get(this.projectTrackers, this.tracker.id);
    this.autoRefreshPreferenceKey = `auto-refresh-${this.tracker.id}`;
    this.userService.getPreference(this.autoRefreshPreferenceKey, 1).then(
      (pref: any) => {
        this.autoRefresh = [true, 'true', 1, "1"].includes(pref.preferenceValue) ? 1 : 0;
      },
      () => { /* preference read failed — the default set above stands */ }
    );

    this.nobody = this.userService.getNobody();
    this.assignees = _.concat(_.get(this.tracker, 'rel.assignees', []), this.nobody);
    this.pageSize = this.stateService.params.page_size || 20;

    if (_.isEmpty(this.options)) {
      this.options = {};
    }
    _.defaults(this.options, { showToolbar: true, addModal: false, editModal: false });

    if (this.options.showCheckboxes === false) {
      this.acls = Object.assign({}, this.acls, { canAssign: false });
    }

    this.kanbanOptions = this.kanbanStrategyService.getOptions(this.stateService.params, this.tracker, this.acls);

    this.setupQueue();
  }

  moveCard(params) {
    let {card, col, row} = params;
    if (!this.kanbanStrategyService.isChanged(this.kanbanOptions, card, col, row)) return;

    card.updating = true;
    this.trackeritemService.saveTi(card, this.kanbanStrategyService.getUpdateObject(this.kanbanOptions, col, row))
      .then((ti: any) => {
        this.trackeritems = this.trackeritems.map((item) => (item.id === ti.id ? ti : item));
      })
      .catch((response) => {
        this.uiService.message.showWarning(
          this.kanbanStrategyService.getKanbanMessage(_.get(response, 'data', {}), this.tracker),
          this.translateService.translate("Update Failed")
        );
      })
      .finally(() => {
        card.updating = false;
      });
  }

  setPageSize(page_size: number) {
    this.pageSize = page_size;
    this.setParam('page_size', page_size, true);
  }

  openTrackerItemAddModal(col, row?) {
    const addModalParams = {
      section: '',
      id: 'ti-add',
      title: "Add new item",
      subtitle: "",
      footerMessage: ""
    };

    const extraData: any = {
      extraFields: {},
      rel: { assignee: [] }
    };

    if (col) {
      extraData.extraFields.status = col;
    }

    if (row) {
      extraData.rel.assignee = [row];
    }

    const modalRef = this.modalService.open(TrackeritemAddModalComponent);
    modalRef.componentInstance.tracker = this.tracker;
    modalRef.componentInstance.trackers = [this.tracker];
    modalRef.componentInstance.customTrackeritem = (data, original) => {
      _.merge(original, data);
      return data;
    };
    modalRef.componentInstance.extraData = extraData;
    modalRef.componentInstance.params = addModalParams;
    modalRef.componentInstance.onClose.subscribe(() => {
	    modalRef.close();
    });
    modalRef.componentInstance.onCreate.subscribe(() => {
	    this.onUpdate.emit();
      modalRef.close();
    });
  }

  toggleKanbanViewMode() {
    const curMode = this.getKanbanViewMode();
    const newMode = (curMode === 'assignee') ? '' : 'assignee';
    this.setKanbanViewMode(newMode);
  }

  tryRefresh() {
    if (this.autoRefresh) {
      this.onRefresh.emit();
    }
  }

  updateAutoRefresh($ev) {
    this.userService.setPreference(this.autoRefreshPreferenceKey, $ev.settingValue)
    .then(
      (response) => {
        const newValue = $ev.settingValue ? "on" : "off";
        this.uiService.message.showSuccess(
          "Auto-Refresh is now " + newValue, "Auto-Refresh"
        );
        this.autoRefresh = $ev.settingValue;
      },
      (reject) => {
        console.log(reject);
        this.uiService.message.showError(
          "Sorry, could not update Auto-Refresh preference", "Auto-Refresh"
        );
      }
    );
  }

  selectedTrackerItems() {
    return this.selectedItems.length ? this.trackeritems.filter(item => this.selectedItems.includes(item.id)) : [];
  }

  onSetFieldValue(event) {
    this.setFieldValueAndSave(event.item, event.fieldName, event.fieldValue);
  }

  setFieldValueAndSave(trackeritem, alias, fieldSetting) {
    let promise;

    switch (alias) {
      case 'status':
        promise = this.trackeritemService.setStatus(trackeritem, fieldSetting);
        break;

      case 'priority':
        promise = this.trackeritemService.setPriority(trackeritem, fieldSetting);
        break;

      case 'assignee':
        promise = this.trackeritemService.setAssignee(trackeritem, fieldSetting);
        break;
    }

    this.updateSubmitted = true;
    promise
      .then(() => this.tryRefresh())
      .catch((err) => {
        this.updateSubmitted = false;
        const error_message = err.overwrite
          ? this.translateService.translate("Could not update [#{{id}}] - {{msg}}", { id: trackeritem.id, msg: 'Another user may have updated the same item' })
          : this.translateService.translate("Could not update [#{{id}}] - {{msg}}", { id: trackeritem.id, msg: 'One or more validations failed' });
        this.uiService.message.showError(error_message, this.translateService.translate('Update Failed'));
      });
  }

  editCard(card) {
    card.tracker = this.tracker;
    this.openTrackeritemEditModal(card);
  }

  openTrackeritemEditModal(item) {
    const modalRef = this.modalService.open(TrackeritemEditModalComponent);
    modalRef.componentInstance.tracker = item.tracker;
    modalRef.componentInstance.trackeritem = item;
    modalRef.componentInstance.allowUpdate = this.canUpdate(item.tracker.id);
    modalRef.componentInstance.allowAssignments = this.canAssign(item.tracker.id);
    modalRef.componentInstance.onUpdate.subscribe(() => {
	    modalRef.close();
    });
    modalRef.componentInstance.onCancel.subscribe(() => {
	    modalRef.close();
    });
    modalRef.componentInstance.onDelete.subscribe(() => {
      this.onRefresh.emit();
	    modalRef.close();
    });
  }

  setParam(param: string, value: any, refresh: boolean = false) {
    const params = {
      [param]: value
    };

    this.stateService.go('.', params, {
      notify: true,
      reload: false
    })
    .then(() => {
      if (refresh) {
        this.onRefresh.emit();
      }
    });
  }

  setupQueue() {
    this.trackeritemQueueService.getQueue();
  }

  protected getKanbanViewMode() {
    return this.stateService.params.r || '';
  }

  private setKanbanViewMode(viewMode: string) {
    this.stateService.go('.', { r: viewMode }, {
      notify: false,
      reload: false,
      location: 'replace'
    }).then(() => {
      const nextParams = { ...this.stateService.params, r: viewMode };
      this.kanbanOptions = this.kanbanStrategyService.getOptions(nextParams, this.tracker, this.acls);
    });
  }

  private canUpdate(trackerId: number) {
    return this.trackerService.acl.get(this.projectTrackers, trackerId).canUpdate;
  }

  private canAssign(trackerId: number) {
    return this.trackerService.acl.get(this.projectTrackers, trackerId).canAssign;
  }

  public pagerCallback(params: any): void {
    this.stateService.go('.', params, { inherit: true, notify: true, reload: false })
      .then(() => {
        this.onRefresh.emit();
    });
  }

  public setSelectedItems(event) {
    this.selectedItems = _.get(event, 'items', []);
  }

  public refresh() {
    this.onRefresh.emit();
  }
}
